<template>
  <div class="relative min-h-screen bg-slate-50 selection:bg-slate-900 selection:text-white pb-24 overflow-hidden">
    <Head>
      <Title>{{ $t('meta.contactTitle') }}</Title>
      <Meta name="description" :content="$t('meta.contactDescription')" />
    </Head>

    <!-- Modern Grid Background -->
    <div class="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-40 pointer-events-none"></div>

    <!-- Edge-to-Edge Hero Banner (Connected to Header) -->
    <div class="relative w-full h-[280px] lg:h-[340px] bg-slate-900 border-b border-slate-800">
      <img
        src="/images/contact_hero.png"
        alt="Technomac Industrial Machinery"
        class="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-[20s] hover:scale-105 ease-out"
      />
      <div class="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/50 to-slate-900/90"></div>
      
      <div class="absolute inset-0 flex flex-col justify-center items-center text-center px-4 pt-12 lg:pt-16 z-10">
        <h1 class="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-4 drop-shadow-xl max-w-4xl">
          {{ $t('contact.hero.title') }}
        </h1>
        <p class="text-base md:text-lg lg:text-xl text-slate-300 font-medium max-w-2xl drop-shadow-md">
          {{ $t('contact.hero.subtitle') || "Let's build something great together. Our team is ready to assist you." }}
        </p>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 lg:-mt-24 z-20">
      
      <div class="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        
        <!-- Contact Information (Left Side on Desktop) -->
        <div class="lg:col-span-5 space-y-8 order-2 lg:order-1">
          
          <!-- Contact Cards (More apparent borders) -->
          <div class="bg-white rounded-[2rem] p-6 sm:p-10 border-2 border-slate-200 shadow-xl shadow-slate-200/50">
            <h3 class="text-xl font-extrabold text-slate-900 mb-8 flex items-center gap-3">
              <i class="fa-solid fa-address-card text-blue-500"></i>
              {{ $t('contact.info.address') || 'Contact Information' }}
            </h3>
            
            <div class="space-y-6">
              <a :href="`mailto:${CONTACT_INFO.email}`" class="flex items-start gap-5 group">
                <div class="w-12 h-12 shrink-0 rounded-2xl bg-slate-50 border-2 border-slate-100 flex items-center justify-center text-slate-500 group-hover:text-blue-600 group-hover:border-blue-200 group-hover:bg-blue-50 transition-all duration-300 shadow-sm group-hover:shadow-md">
                  <i class="fa-regular fa-envelope text-lg"></i>
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{{ $t('contact.quickLinks.emailLabel') }}</p>
                  <p class="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{{ CONTACT_INFO.email }}</p>
                </div>
              </a>

              <div class="h-px w-full bg-slate-100"></div>

              <a :href="`tel:${CONTACT_INFO.phone}`" class="flex items-start gap-5 group">
                <div class="w-12 h-12 shrink-0 rounded-2xl bg-slate-50 border-2 border-slate-100 flex items-center justify-center text-slate-500 group-hover:text-blue-600 group-hover:border-blue-200 group-hover:bg-blue-50 transition-all duration-300 shadow-sm group-hover:shadow-md">
                  <i class="fa-solid fa-phone text-lg"></i>
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{{ $t('contact.quickLinks.phoneLabel') }}</p>
                  <p class="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{{ CONTACT_INFO.phone }}</p>
                </div>
              </a>

              <div class="h-px w-full bg-slate-100"></div>

              <a :href="whatsappLink" target="_blank" class="flex items-start gap-5 group">
                <div class="w-12 h-12 shrink-0 rounded-2xl bg-slate-50 border-2 border-slate-100 flex items-center justify-center text-slate-500 group-hover:text-emerald-500 group-hover:border-emerald-200 group-hover:bg-emerald-50 transition-all duration-300 shadow-sm group-hover:shadow-md">
                  <i class="fa-brands fa-whatsapp text-2xl"></i>
                </div>
                <div>
                  <p class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{{ $t('contact.quickLinks.whatsappLabel') }}</p>
                  <p class="text-base font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">{{ $t('contact.quickLinks.startChat') }}</p>
                </div>
              </a>
            </div>
          </div>

          <!-- Modern Map (Colored, thick border) -->
          <div class="rounded-[2rem] overflow-hidden border-2 border-slate-200 h-64 bg-slate-100 relative group shadow-xl shadow-slate-200/50">
            <iframe
              src="https://maps.google.com/maps?q=Corso+Chiaiano+28+Napoli+80145+Italia&t=&z=15&ie=UTF8&iwloc=&output=embed"
              class="w-full h-full border-0 opacity-90 group-hover:opacity-100 transition-all duration-700"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              title="Technomac SNC Location"
            ></iframe>
          </div>

        </div>

        <!-- Form (Right Side on Desktop) -->
        <div class="lg:col-span-7 order-1 lg:order-2" id="contact-form">
          <div class="bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 p-6 sm:p-10 border-2 border-slate-200 relative overflow-hidden">
            
            <h2 class="text-2xl font-extrabold text-slate-900 mb-2">{{ $t('contact.form.title') }}</h2>
            <p class="text-slate-500 font-medium mb-8">{{ $t('contact.form.subtitle') }}</p>
            
            <form @submit.prevent="handleSubmit" class="space-y-6">
              
              <!-- Modern Segmented Control -->
              <div class="flex p-1.5 bg-slate-100 rounded-xl mb-8 border border-slate-200">
                <button type="button" @click="form.inquiry_type = 'general'"
                  class="flex-1 py-3 text-sm font-bold rounded-lg transition-all duration-300"
                  :class="form.inquiry_type === 'general' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'">
                  {{ $t('contact.form.generalInquiry') }}
                </button>
                <button type="button" @click="form.inquiry_type = 'sell'"
                  class="flex-1 py-3 text-sm font-bold rounded-lg transition-all duration-300"
                  :class="form.inquiry_type === 'sell' ? 'bg-white text-emerald-600 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'">
                  {{ $t('contact.form.sellMachine') }}
                </button>
              </div>

              <!-- Creative & User Friendly Fields -->
              <div class="grid sm:grid-cols-2 gap-6">
                <div class="space-y-2 group">
                  <label class="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{{ $t('contact.form.firstName') }} <span class="text-red-500">*</span></label>
                  <input v-model="form.first_name" type="text" required
                         class="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                         :placeholder="$t('contact.form.firstNamePlaceholder')" />
                </div>
                <div class="space-y-2 group">
                  <label class="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{{ $t('contact.form.lastName') }} <span class="text-red-500">*</span></label>
                  <input v-model="form.last_name" type="text" required
                         class="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                         :placeholder="$t('contact.form.lastNamePlaceholder')" />
                </div>
              </div>

              <div class="grid sm:grid-cols-2 gap-6">
                <div class="space-y-2 group">
                  <label class="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{{ $t('contact.form.email') }} <span class="text-red-500">*</span></label>
                  <input v-model="form.email" type="email" required
                         class="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                         :placeholder="$t('contact.form.emailPlaceholder')" />
                </div>
                <div class="space-y-2 group">
                  <label class="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{{ $t('contact.form.phone') }}</label>
                  <input v-model="form.phone" type="tel"
                         class="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                         :placeholder="$t('contact.form.phonePlaceholder')" />
                </div>
              </div>

              <!-- Sell Machine Section -->
              <transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 -translate-y-4 scale-95"
                enter-to-class="opacity-100 translate-y-0 scale-100"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0 scale-100"
                leave-to-class="opacity-0 -translate-y-4 scale-95"
              >
                <div v-if="form.inquiry_type === 'sell'" class="p-6 bg-emerald-50 rounded-2xl border-2 border-emerald-200 space-y-6">
                  <div class="space-y-2">
                    <label class="text-xs font-bold text-emerald-700 uppercase tracking-widest ml-1">{{ $t('contact.form.machineDetails') }} <span class="text-red-500">*</span></label>
                    <textarea v-model="form.machine_details" rows="3" required
                              class="w-full px-4 py-3.5 bg-white border-2 border-emerald-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium resize-none"
                              :placeholder="$t('contact.form.machineDetailsPlaceholder')"></textarea>
                  </div>
                  
                  <div class="space-y-2">
                    <label class="text-xs font-bold text-emerald-700 uppercase tracking-widest ml-1">{{ $t('contact.form.priceExpectation') }}</label>
                    <div class="relative">
                      <span class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold">€</span>
                      <input v-model="form.expected_price" type="text"
                             class="w-full pl-9 pr-4 py-3.5 bg-white border-2 border-emerald-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all font-medium"
                             :placeholder="$t('contact.form.optional')" />
                    </div>
                  </div>

                  <div class="space-y-2">
                    <label class="text-xs font-bold text-emerald-700 uppercase tracking-widest ml-1">{{ $t('contact.form.uploadPhotos') }}</label>
                    <div @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop"
                         class="border-2 border-dashed border-emerald-300 rounded-xl p-6 sm:p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-white transition-all bg-emerald-50/50 group">
                      <input ref="fileInputRef" type="file" multiple accept="image/*" class="hidden" @change="handleFileChange" />
                      <div class="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                        <i class="fa-solid fa-cloud-arrow-up text-xl text-emerald-600"></i>
                      </div>
                      <p class="font-bold text-emerald-800">{{ $t('contact.form.uploadInstruction') }}</p>
                      <p class="text-sm text-emerald-600/70 mt-1 font-medium">Drag & drop or click to browse (Max 5)</p>
                    </div>
                    
                    <!-- Previews -->
                    <div v-if="files.length" class="flex flex-wrap gap-3 mt-4">
                      <div v-for="(file, i) in files" :key="i" class="relative group mt-2">
                        <img :src="getFilePreview(file)" class="w-16 h-16 object-cover rounded-lg border-2 border-emerald-200 shadow-sm" />
                        <button type="button" @click.stop="removeFile(i)"
                                class="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110 shadow-md">
                          <i class="fa-solid fa-times"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>

              <!-- Message -->
              <div class="space-y-2 group">
                <label class="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">{{ $t('contact.form.message') }} <span class="text-red-500">*</span></label>
                <textarea v-model="form.message" rows="5" required
                          class="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium resize-none"
                          :placeholder="$t('contact.form.messagePlaceholder')"></textarea>
              </div>

              <!-- Submit Button -->
              <button type="submit" :disabled="isSubmitting"
                      class="w-full py-4 mt-4 bg-slate-900 hover:bg-blue-600 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-xl shadow-slate-900/20 hover:shadow-blue-600/30 active:scale-[0.98] group">
                <i v-if="isSubmitting" class="fa-solid fa-circle-notch fa-spin text-lg"></i>
                <i v-else class="fa-regular fa-paper-plane text-lg group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform"></i>
                {{ isSubmitting ? $t('contact.form.sending') : $t('contact.form.sendMessage') }}
              </button>

            </form>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSweetAlert } from '~/composables/useSweetAlert'

const CONTACT_INFO = {
  phone: '+393289722811',
  alt_phone: '+33668407405',
  email: 'info@technomac-snc.com',
  address: 'Corso Chiaiano, 28 Napoli 80145-ITALIA'
} as const

const { t } = useI18n()
const route = useRoute()
const sweetAlert = useSweetAlert()

interface ContactForm {
  inquiry_type: 'general' | 'sell'
  first_name: string
  last_name: string
  email: string
  phone: string
  machine_details: string
  expected_price: string
  message: string
}

const form = ref<ContactForm>({
  inquiry_type: 'general',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  machine_details: '',
  expected_price: '',
  message: ''
})

const isSubmitting = ref(false)
const files = ref<File[]>([])
const fileInputRef = ref<HTMLInputElement | null>(null)

const whatsappLink = computed(() => {
  const phone = CONTACT_INFO.phone.replace(/\D/g, '')
  return `https://wa.me/${phone}`
})

const faqs = computed(() => [
  { question: t('contact.faq.items.machinery.question'), answer: t('contact.faq.items.machinery.answer') },
  { question: t('contact.faq.items.response.question'), answer: t('contact.faq.items.response.answer') },
  { question: t('contact.faq.items.selling.question'), answer: t('contact.faq.items.selling.answer') }
])

const triggerFileInput = () => fileInputRef.value?.click()
const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  addFiles(Array.from(target.files || []))
}
const handleDrop = (e: DragEvent) => {
  addFiles(Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/')))
}
const addFiles = (newFiles: File[]) => {
  if (files.value.length + newFiles.length > 5) {
    sweetAlert.showError(t('contact.form.errorTitle'), t('contact.form.tooManyFiles'))
    return
  }
  files.value = [...files.value, ...newFiles].slice(0, 5)
}
const getFilePreview = (file: File) => URL.createObjectURL(file)
const removeFile = (i: number) => files.value.splice(i, 1)

const resetForm = () => {
  form.value = { inquiry_type: 'general', first_name: '', last_name: '', email: '', phone: '', machine_details: '', expected_price: '', message: '' }
  files.value = []
}

const handleSubmit = async () => {
  isSubmitting.value = true
  try {
    const formData = new FormData()
    formData.append('name', `${form.value.first_name} ${form.value.last_name}`.trim())
    formData.append('email', form.value.email)
    formData.append('subject', form.value.inquiry_type === 'sell' ? t('contact.form.subjectSell') : t('contact.form.subjectGeneral'))
    formData.append('message', form.value.message)
    formData.append('inquiry_type', form.value.inquiry_type)
    if (form.value.phone) formData.append('phone', form.value.phone)
    if (form.value.inquiry_type === 'sell') {
      if (form.value.machine_details) formData.append('machine_details', form.value.machine_details)
      if (form.value.expected_price) formData.append('expected_price', form.value.expected_price)
    }
    files.value.forEach((file: File, i: number) => formData.append(`attachments[${i}]`, file))

    const config = useRuntimeConfig()
    const response = await fetch(`${config.public.apiBase}/contact/general`, {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' }
    })

    if (!response.ok) throw new Error('Failed')
    sweetAlert.showSuccess(t('contact.form.successTitle'), form.value.inquiry_type === 'sell' ? t('contact.form.sellSuccessMessage') : t('contact.form.generalSuccessMessage'))
    resetForm()
  } catch {
    sweetAlert.showError(t('contact.form.errorTitle'), t('contact.form.errorMessage'))
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  const inquiry = route.query.inquiry as string
  if (inquiry === 'sell' || inquiry === 'general') form.value.inquiry_type = inquiry
  if (route.hash === '#contact-form') {
    setTimeout(() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
  }
})

useHead({
  title: t('meta.contactTitle'),
  meta: [{ name: 'description', content: t('meta.contactDescription') }]
})
</script>
